import { mapWithConcurrency } from '../../../shared/asyncPool.js';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';
import {
  RECORD_LOGO_ALLOWED_TYPES,
  RECORD_LOGO_MAX_BYTES,
  removeRecordLogo,
  uploadRecordLogo,
  validateRecordLogoFile,
} from './recordLogos';
import { calculateEventCustomerTotalAmount } from './events';
import {
  clientDetailToUpsertInput,
  rowToClientDetail,
  rowToClientSummary,
  rowToClientListRow,
  type ClientDetail,
  type ClientSummary,
  type ClientListRow,
} from '../domain/clients';

export type { ClientSummary };

export type ClientBasicRecord = {
  id: string;
  business_name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  tax_status?: string | null;
  notes?: string | null;
  tags?: string[] | null;
  sales_person_profile_id?: string | null;
  marketing_segments?: string[] | null;
  is_management_co?: boolean | null;
  is_venue_owner?: boolean | null;
  payment_terms?: string | null;
  billing_contact_id?: string | null;
  tax_exempt?: boolean | null;
  tax_exempt_expires_on?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  account_status?: string | null;
  account_status_since?: string | null;
  logo_storage_path?: string | null;
  logo_public_url?: string | null;
  logo_alt_text?: string | null;
  logo_uploaded_at?: string | null;
  logo_uploaded_by?: string | null;
};

type ClientOption = { supabase?: SupabaseClient };
export type ClientListSortKey = 'name' | 'location' | 'state' | 'type' | 'created' | 'revenue';
export type ClientListSortDir = 'asc' | 'desc';
export type ClientListFilters = {
  search?: string;
  limit?: number;
  offset?: number;
  pageSize?: number;
  sortKey?: ClientListSortKey;
  sortDir?: ClientListSortDir;
  typeFilter?: string;
  locationFilter?: 'all' | 'with' | 'without';
  recentOnly?: boolean;
};

const defaultSelect = 'id,business_name,tax_status,contacts,notes,metadata';

const CLIENT_SEARCH_LIMIT = 20;

// `get_clients_library_page` returns a jsonb envelope rather than a row set, so
// both the library list and the type-ahead have to unwrap it the same way.
function unwrapClientsLibraryPage(
  data: unknown,
): { rows: unknown[]; count: number | null } {
  const payload = data && typeof data === 'object' && !Array.isArray(data)
    ? data as { data?: unknown; count?: unknown }
    : null;
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const rawCount = payload?.count;
  const parsedCount = typeof rawCount === 'number' ? rawCount : Number(rawCount ?? 0);
  return { rows, count: Number.isFinite(parsedCount) ? parsedCount : null };
}

export async function fetchClientSearchResults(
  query: string,
  options?: ClientOption,
): Promise<{ data: ClientSummary[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trimmed = query.trim();
  if (!trimmed) return { data: [], error: null };
  // Goes through a SECURITY DEFINER RPC rather than selecting from
  // `active_clients`. That view is security_invoker, so the `clients` RLS policy
  // (`is_current_actor_internal_staff()`) is evaluated once per row *scanned*.
  // ILIKE is not leakproof, so Postgres may not apply the name filter ahead of
  // the security qual — the trigram index becomes unusable and the auth helper
  // runs against all 27k rows, taking 106s against an 8s statement_timeout. That
  // failed this search for every role, admins included. The RPC checks the same
  // gate exactly once, then scans as owner.
  //
  // Ranked exact → prefix → contains, so the 20-row cap is both deterministic
  // and relevant. Without ranking, the customer you were looking for could
  // simply never appear — and reappear on the next identical search.
  const { data, error } = await supabase.rpc('search_clients', {
    p_search: trimmed,
    p_limit: CLIENT_SEARCH_LIMIT,
  });
  if (error) return { data: [], error };
  const { rows } = unwrapClientsLibraryPage(data);
  return { data: rows.map(rowToClientSummary), error: null };
}

export async function fetchClientById(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientDetail | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('clients')
    .select(defaultSelect)
    .eq('id', clientId)
    .maybeSingle();
  return { data: data ? rowToClientDetail(data) : null, error: error ?? null };
}

// Read only the returned library IDs, avoiding another unbounded customer scan.
// Both the paged list and pinned favorites use this same address hydration.
async function hydrateClientListAddresses(
  rows: unknown[],
  supabase: SupabaseClient,
): Promise<{ data: ClientListRow[]; error: PostgrestError | null }> {
  const clients = rows.map(rowToClientListRow);
  if (clients.length === 0) return { data: [], error: null };
  const ids = Array.from(new Set(clients.map(client => client.id)));
  const { data, error } = await supabase
    .from('clients')
    .select('id,address_line1,address_line2')
    .in('id', ids)
    .is('deleted_at', null);
  if (error) return { data: [], error };
  const addresses = new Map((data ?? []).map(row => [row.id, rowToClientListRow(row)]));
  return {
    data: clients.map(client => ({
      ...client,
      addressLine1: addresses.get(client.id)?.addressLine1 ?? null,
      addressLine2: addresses.get(client.id)?.addressLine2 ?? null,
    })),
    error: null,
  };
}

export async function fetchClientsList(
  filters: ClientListFilters = {},
  options?: ClientOption,
): Promise<{ data: ClientListRow[]; count: number | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const {
    search = '',
    limit,
    offset,
    pageSize,
    sortKey = 'name',
    sortDir = 'asc',
    typeFilter = 'all',
    locationFilter = 'all',
    recentOnly = false,
  } = filters;
  const effectivePageSize = pageSize ?? limit ?? 50;
  const effectiveOffset = offset ?? 0;
  const { data, error } = await supabase.rpc('get_clients_library_page', {
    p_search: search.trim() || null,
    p_offset: Math.max(0, effectiveOffset),
    p_limit: Math.min(Math.max(1, effectivePageSize), 100),
    p_sort_key: sortKey,
    p_sort_dir: sortDir,
    p_type_filter: typeFilter,
    p_location_filter: locationFilter,
    p_recent_only: recentOnly,
  });
  if (error) {
    return { data: [], count: null, error };
  }
  const { rows, count } = unwrapClientsLibraryPage(data);
  const result = await hydrateClientListAddresses(rows, supabase);
  return { ...result, count: result.error ? null : count };
}

export async function fetchFavoriteClientRows(
  clientIds: string[],
  options?: ClientOption,
): Promise<{ data: ClientListRow[]; error: PostgrestError | null }> {
  const uniqueIds = Array.from(new Set(clientIds.filter(Boolean))).slice(0, 100);
  if (uniqueIds.length === 0) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_clients_library_records', {
    p_client_ids: uniqueIds,
  });
  if (error) return { data: [], error };
  const { rows } = unwrapClientsLibraryPage(data);
  return hydrateClientListAddresses(rows, supabase);
}

export async function upsertClient(
  detail: ClientDetail,
  options?: ClientOption,
): Promise<{ data: ClientDetail | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = clientDetailToUpsertInput(detail);
  const { data, error } = await supabase.from('clients').upsert(payload).select(defaultSelect).maybeSingle();
  return { data: data ? rowToClientDetail(data) : null, error: error ?? null };
}

export async function createClient(
  businessName: string,
  options?: ClientOption,
): Promise<{ data: ClientSummary | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trimmed = businessName.trim();
  if (!trimmed) return { data: null, error: null };
  const { data, error } = await supabase
    .from('clients')
    .insert({ business_name: trimmed })
    .select('id,business_name,tax_status')
    .maybeSingle();
  return { data: data ? rowToClientSummary(data) : null, error: error ?? null };
}

export type ClientQuickCreateInput = {
  businessName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export async function createClientQuick(
  input: ClientQuickCreateInput,
  options?: ClientOption,
): Promise<{ data: ClientSummary | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const businessName = input.businessName.trim();
  if (!businessName) return { data: null, error: null };
  const { data, error } = await supabase
    .from('clients')
    .insert({
      business_name: businessName,
      address_line1: input.addressLine1?.trim() || null,
      address_line2: input.addressLine2?.trim() || null,
      city: input.city?.trim() || null,
      state: input.state?.trim() || null,
      postal_code: input.postalCode?.trim() || null,
      country: input.country?.trim() || null,
    })
    .select('id,business_name,city,state,tax_status')
    .maybeSingle();
  return { data: data ? rowToClientSummary(data) : null, error: error ?? null };
}

export async function softDeleteClient(
  clientId: string,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('soft_delete', { table_name: 'clients', _id: clientId });
  return error ?? null;
}

const CLIENT_BASICS_COLUMNS = [
  'id',
  'business_name',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'country',
  'tax_status',
  'notes',
  'tags',
  'sales_person_profile_id',
  'marketing_segments',
  'is_management_co',
  'is_venue_owner',
  'payment_terms',
  'tax_exempt',
  'tax_exempt_expires_on',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'logo_storage_path',
  'logo_public_url',
  'logo_alt_text',
  'logo_uploaded_at',
  'logo_uploaded_by',
];
const CLIENT_ACCOUNT_STATUS_COLUMNS = ['account_status', 'account_status_since'];
const CLIENT_BILLING_CONTACT_COLUMN = 'billing_contact_id';

export async function fetchClientBasics(
  clientId: string,
  options?: ClientOption & { includeAccountStatus?: boolean },
): Promise<{ data: ClientBasicRecord | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const optionalColumns = options?.includeAccountStatus === false
    ? [CLIENT_BILLING_CONTACT_COLUMN]
    : [...CLIENT_ACCOUNT_STATUS_COLUMNS, CLIENT_BILLING_CONTACT_COLUMN];
  const { data, error } = await supabase
    .from('clients')
    .select([...CLIENT_BASICS_COLUMNS, ...optionalColumns].join(','))
    .eq('id', clientId)
    .maybeSingle();
  // Preserve billing-contact data when older databases lack the independent
  // account-status feature. If billing_contact_id is also absent, fall back to
  // the stable base record.
  if (error && error.code === '42703') {
    const retryColumns = options?.includeAccountStatus === false
      ? CLIENT_BASICS_COLUMNS
      : [...CLIENT_BASICS_COLUMNS, CLIENT_BILLING_CONTACT_COLUMN];
    let fallback = await supabase
      .from('clients')
      .select(retryColumns.join(','))
      .eq('id', clientId)
      .maybeSingle();
    if (fallback.error?.code === '42703' && retryColumns.length > CLIENT_BASICS_COLUMNS.length) {
      fallback = await supabase
        .from('clients')
        .select(CLIENT_BASICS_COLUMNS.join(','))
        .eq('id', clientId)
        .maybeSingle();
    }
    return { data: (fallback.data as ClientBasicRecord | null) ?? null, error: fallback.error ?? null };
  }
  return { data: (data as ClientBasicRecord | null) ?? null, error: error ?? null };
}

export async function updateClientBasics(
  clientId: string,
  patch: Partial<ClientBasicRecord>,
  options?: ClientOption,
): Promise<{ data: Pick<ClientBasicRecord, 'updated_at' | 'updated_by'> | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Sign in again to save customer changes.');
  if (patch.billing_contact_id) {
    const { data: billingContact, error: billingContactError } = await supabase
      .from('client_contacts')
      .select('id')
      .eq('id', patch.billing_contact_id)
      .eq('client_id', clientId)
      .is('deleted_at', null)
      .maybeSingle();
    if (billingContactError) return { data: null, error: billingContactError };
    if (!billingContact) {
      return {
        data: null,
        error: {
          message: 'Billing contact must belong to this client.',
          details: '',
          hint: '',
          code: 'CLIENT_BILLING_CONTACT_MISMATCH',
          name: 'PostgrestError',
        } as PostgrestError,
      };
    }
  }
  const { data, error } = await supabase
    .from('clients')
    .update({ ...patch, updated_by: user.id })
    .eq('id', clientId)
    .select('updated_at,updated_by')
    .maybeSingle();
  return { data: (data as any) ?? null, error: error ?? null };
}

// Customer logos use the shared record-logo helpers (recordLogos.ts) with the client-logos bucket.
export const CLIENT_LOGO_MAX_BYTES = RECORD_LOGO_MAX_BYTES;
export const CLIENT_LOGO_ALLOWED_TYPES = RECORD_LOGO_ALLOWED_TYPES;

export type ClientLogoRecord = Pick<
  ClientBasicRecord,
  'logo_storage_path' | 'logo_public_url' | 'logo_alt_text' | 'logo_uploaded_at' | 'logo_uploaded_by'
>;

export const validateClientLogoFile = validateRecordLogoFile;

export async function uploadClientLogo(params: {
  clientId: string;
  businessName: string;
  file: File;
  previousStoragePath?: string | null;
  uploadedByProfileId?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  data: ClientLogoRecord | null;
  error: StorageError | PostgrestError | Error | null;
  cleanupError: StorageError | null;
}> {
  return uploadRecordLogo({
    bucket: 'client-logos',
    table: 'clients',
    recordId: params.clientId,
    displayName: params.businessName,
    fallbackName: 'Customer',
    file: params.file,
    previousStoragePath: params.previousStoragePath,
    uploadedByProfileId: params.uploadedByProfileId,
    supabase: params.supabase,
  });
}

export async function removeClientLogo(params: {
  clientId: string;
  storagePath?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  error: StorageError | PostgrestError | null;
  cleanupError: StorageError | null;
}> {
  return removeRecordLogo({
    bucket: 'client-logos',
    table: 'clients',
    recordId: params.clientId,
    storagePath: params.storagePath,
    supabase: params.supabase,
  });
}

export async function fetchClientDiscount(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: number | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('clients').select('set_discount_percent').eq('id', clientId).maybeSingle();
  if (error) return { data: null, error };
  const raw = (data as any)?.set_discount_percent;
  return { data: typeof raw === 'number' ? raw : null, error: null };
}

export async function updateClientDiscount(
  clientId: string,
  discountPercent: number | null,
  options?: ClientOption,
): Promise<{ data: number | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('clients')
    .update({ set_discount_percent: discountPercent })
    .eq('id', clientId)
    .select('set_discount_percent')
    .maybeSingle();
  if (error) return { data: null, error };
  const raw = (data as any)?.set_discount_percent;
  return { data: typeof raw === 'number' ? raw : null, error: null };
}

export type ClientTaxExemption = { taxExempt: boolean; expiresOn: string | null; verifiedAt: string | null; certificateId: string | null };
/** The customer's own tax exemption. An exempt customer's service agreement locations pay no tax. */
export async function fetchClientTaxExemption(clientId: string, options?: ClientOption): Promise<ClientTaxExemption> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('clients')
    .select('tax_exempt,tax_exempt_expires_on,tax_exempt_verified_at,tax_exempt_certificate_id').eq('id', clientId).maybeSingle();
  if (error) throw error;
  const row = (data ?? {}) as { tax_exempt?: boolean | null; tax_exempt_expires_on?: string | null; tax_exempt_verified_at?: string | null; tax_exempt_certificate_id?: string | null };
  return { taxExempt: row.tax_exempt === true, expiresOn: row.tax_exempt_expires_on ?? null, verifiedAt: row.tax_exempt_verified_at ?? null, certificateId: row.tax_exempt_certificate_id ?? null };
}

/** The customer's purchase order settings for service agreement proposals. */
export async function fetchClientPurchaseOrderSettings(clientId: string, options?: ClientOption): Promise<{ mode: 'none' | 'optional' | 'required'; blanketNumber: string | null; blanketExpiresOn: string | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('clients').select('purchase_order_mode,blanket_po_number,blanket_po_expires_on').eq('id', clientId).maybeSingle();
  if (error) throw error;
  const row = (data ?? {}) as { purchase_order_mode?: string | null; blanket_po_number?: string | null; blanket_po_expires_on?: string | null };
  const mode = row.purchase_order_mode === 'optional' || row.purchase_order_mode === 'required' ? row.purchase_order_mode : 'none';
  return { mode, blanketNumber: row.blanket_po_number ?? null, blanketExpiresOn: row.blanket_po_expires_on ?? null };
}

export async function fetchClientCreditLimit(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: number | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('clients').select('credit_limit').eq('id', clientId).maybeSingle();
  if (error) return { data: null, error };
  const raw = (data as any)?.credit_limit;
  return { data: typeof raw === 'number' ? raw : null, error: null };
}

export async function updateClientCreditLimit(
  clientId: string,
  creditLimit: number | null,
  options?: ClientOption,
): Promise<{ data: number | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('clients')
    .update({ credit_limit: creditLimit })
    .eq('id', clientId)
    .select('credit_limit')
    .maybeSingle();
  if (error) return { data: null, error };
  const raw = (data as any)?.credit_limit;
  return { data: typeof raw === 'number' ? raw : null, error: null };
}

export type ClientStatementSummary = {
  outstanding_total: number;
  invoice_count: number;
  oldest_due_date: string | null;
};

export async function fetchClientStatementSummary(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientStatementSummary; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('invoices')
    .select('invoice_id,totals_json,due_date')
    .eq('client_id', clientId)
    .eq('status', 'posted')
    .order('due_date', { ascending: true, nullsFirst: false });
  if (error) return { data: { outstanding_total: 0, invoice_count: 0, oldest_due_date: null }, error };
  const rows = Array.isArray(data) ? data : [];
  let total = 0;
  for (const row of rows) {
    const tj = (row as any)?.totals_json;
    if (tj && typeof tj === 'object' && typeof tj.total === 'number') {
      total += tj.total;
    }
  }
  return {
    data: {
      outstanding_total: total,
      invoice_count: rows.length,
      oldest_due_date: rows.length > 0 ? (rows[0] as any)?.due_date ?? null : null,
    },
    error: null,
  };
}

const detailPageSelect = 'id,business_name,city,state,notes,created_at';

export type ClientDetailRecord = {
  id: string;
  business_name: string | null;
  city?: string | null;
  state?: string | null;
  notes?: string | null;
  created_at?: string | null;
};

export async function fetchClientDetailRecord(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientDetailRecord | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('clients')
    .select(detailPageSelect)
    .eq('id', clientId)
    .is('deleted_at', null)
    .maybeSingle();
  return { data: (data as ClientDetailRecord | null) ?? null, error: error ?? null };
}

export type ClientCounts = { contacts: number; events: number };

export async function fetchClientCounts(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientCounts; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_client_record_counts' as any, { p_client_id: clientId });
  const counts = data as unknown as ClientCounts | null;
  return { data: { contacts: Number(counts?.contacts ?? 0), events: Number(counts?.events ?? 0) }, error };
}

export type ClientBranchOption = { id: string; label: string; position: number | null };

export async function fetchClientBranches(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientBranchOption[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_branches')
    .select('id,branch_name,position')
    .eq('client_id', clientId)
    .is('deleted_at', null)
    .order('position', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true });
  if (error) return { data: [], error };
  const mapped = (data || []).map((row: any) => ({
    id: String(row.id),
    label: row.branch_name || 'Untitled Branch',
    position: row.position ?? null,
  }));
  mapped.sort((a, b) => {
    const ap = a.position ?? Number.MAX_SAFE_INTEGER;
    const bp = b.position ?? Number.MAX_SAFE_INTEGER;
    if (ap !== bp) return ap - bp;
    return a.label.localeCompare(b.label);
  });
  return { data: mapped, error: null };
}

export type ClientBranchDetail = {
  id: string;
  client_id: string;
  branch_name: string;
  billing_address_line1?: string | null;
  billing_address_line2?: string | null;
  billing_city?: string | null;
  billing_state?: string | null;
  billing_postal_code?: string | null;
  billing_country?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_postal_code?: string | null;
  shipping_country?: string | null;
  external_ref?: string | null;
  set_discount_percent?: number | null;
  payment_terms?: string | null;
  position?: number | null;
};

export type ClientContactOption = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  branchIds: string[];
};

type ClientContactOptionRow = {
  id?: string | number | null;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  position?: number | null;
};

type ContactEmailOptionRow = {
  contact_id?: string | number | null;
  email?: string | null;
  is_primary?: boolean | null;
};

type ContactPhoneOptionRow = {
  contact_id?: string | number | null;
  number?: string | null;
  is_primary?: boolean | null;
};

type ContactBranchLinkOptionRow = {
  contact_id?: string | number | null;
  branch_id?: string | number | null;
};

type ClientBranchQuickRow = ClientBranchDetail & {
  id: string | number;
  client_id: string | number;
};

function mapClientContactOption(
  row: ClientContactOptionRow,
  emailMap: Map<string, string>,
  phoneMap: Map<string, string>,
  branchMap: Map<string, string[]>,
): ClientContactOption {
  const id = String(row?.id ?? '');
  return {
    id,
    firstName: row?.first_name ?? null,
    lastName: row?.last_name ?? null,
    title: row?.title ?? null,
    email: row?.email ?? emailMap.get(id) ?? null,
    phone: row?.phone ?? phoneMap.get(id) ?? null,
    branchIds: branchMap.get(id) ?? [],
  };
}

export function clientContactDisplayName(contact: ClientContactOption): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  return name || contact.email || contact.phone || 'Unnamed contact';
}

export async function fetchClientContactOptions(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientContactOption[]; error: PostgrestError | null }> {
  if (!clientId) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_contacts')
    .select('id,first_name,last_name,title,email,phone,position')
    .eq('client_id', clientId)
    .is('deleted_at', null)
    .order('position', { ascending: true, nullsFirst: false })
    .order('last_name', { ascending: true, nullsFirst: false })
    .order('first_name', { ascending: true, nullsFirst: false });
  if (error || !Array.isArray(data)) return { data: [], error: error ?? null };

  const contactRows = data as ClientContactOptionRow[];
  const contactIds = contactRows.map((row) => String(row.id ?? '')).filter(Boolean);
  const [emailResult, phoneResult, branchResult] = contactIds.length
    ? await Promise.all([
        supabase
          .from('contact_emails')
          .select('contact_id,email,is_primary')
          .eq('entity', 'client')
          .in('contact_id', contactIds)
          .is('deleted_at', null),
        supabase
          .from('contact_phones')
          .select('contact_id,number,is_primary')
          .eq('entity', 'client')
          .in('contact_id', contactIds)
          .is('deleted_at', null),
        supabase
          .from('contact_branch_links')
          .select('contact_id,branch_id')
          .in('contact_id', contactIds),
      ])
    : [
        { data: [], error: null },
        { data: [], error: null },
        { data: [], error: null },
      ];
  const firstError = emailResult.error ?? phoneResult.error ?? branchResult.error ?? null;
  if (firstError) return { data: [], error: firstError };

  const emailMap = new Map<string, string>();
  for (const row of (emailResult.data || []) as ContactEmailOptionRow[]) {
    const contactId = row?.contact_id ? String(row.contact_id) : '';
    const email = String(row?.email || '').trim();
    if (!contactId || !email) continue;
    if (row?.is_primary || !emailMap.has(contactId)) emailMap.set(contactId, email);
  }

  const phoneMap = new Map<string, string>();
  for (const row of (phoneResult.data || []) as ContactPhoneOptionRow[]) {
    const contactId = row?.contact_id ? String(row.contact_id) : '';
    const number = String(row?.number || '').trim();
    if (!contactId || !number) continue;
    if (row?.is_primary || !phoneMap.has(contactId)) phoneMap.set(contactId, number);
  }

  const branchMap = new Map<string, string[]>();
  for (const row of (branchResult.data || []) as ContactBranchLinkOptionRow[]) {
    const contactId = row?.contact_id ? String(row.contact_id) : '';
    const branchId = row?.branch_id == null ? '' : String(row.branch_id);
    if (!contactId || !branchId) continue;
    const list = branchMap.get(contactId) ?? [];
    list.push(branchId);
    branchMap.set(contactId, list);
  }

  return {
    data: contactRows.map((row) => mapClientContactOption(row, emailMap, phoneMap, branchMap)),
    error: null,
  };
}

export type BranchContactCounts = Record<string, number>;

export async function fetchClientBranchDetails(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: { branches: ClientBranchDetail[]; contactCounts: BranchContactCounts }; error: PostgrestError | null }> {
  if (!clientId) return { data: { branches: [], contactCounts: {} }, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_branches')
    .select(
      'id,client_id,branch_name,billing_address_line1,billing_address_line2,billing_city,billing_state,billing_postal_code,billing_country,shipping_address_line1,shipping_address_line2,shipping_city,shipping_state,shipping_postal_code,shipping_country,external_ref,set_discount_percent,payment_terms,position',
    )
    .eq('client_id', clientId)
    .is('deleted_at', null)
    .order('position', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true });
  if (error) return { data: { branches: [], contactCounts: {} }, error };
  const branches: ClientBranchDetail[] = (data || []).map((row: any) => ({
    id: String(row.id),
    client_id: String(row.client_id),
    branch_name: row.branch_name || 'Untitled Branch',
    billing_address_line1: row.billing_address_line1 ?? null,
    billing_address_line2: row.billing_address_line2 ?? null,
    billing_city: row.billing_city ?? null,
    billing_state: row.billing_state ?? null,
    billing_postal_code: row.billing_postal_code ?? null,
    billing_country: row.billing_country ?? null,
    shipping_address_line1: row.shipping_address_line1 ?? null,
    shipping_address_line2: row.shipping_address_line2 ?? null,
    shipping_city: row.shipping_city ?? null,
    shipping_state: row.shipping_state ?? null,
    shipping_postal_code: row.shipping_postal_code ?? null,
    shipping_country: row.shipping_country ?? null,
    external_ref: row.external_ref ?? null,
    set_discount_percent:
      row.set_discount_percent != null && !Number.isNaN(Number(row.set_discount_percent))
        ? Number(row.set_discount_percent)
        : null,
    payment_terms: row.payment_terms ?? null,
    position: row.position ?? null,
  }));

  const numericIds = branches.map((row) => Number(row.id)).filter((value) => Number.isFinite(value));
  const contactCounts: BranchContactCounts = {};
  if (numericIds.length) {
    const { data: linkRows, error: countError } = await supabase
      .from('contact_branch_links')
      .select('branch_id,contact_id')
      .in('branch_id', numericIds);
    if (!countError) {
      const accumulator: Record<string, Set<string>> = {};
      for (const row of linkRows || []) {
        const branchId = row?.branch_id == null ? null : String(row.branch_id);
        const contactId = row?.contact_id == null ? null : String(row.contact_id);
        if (!branchId || !contactId) continue;
        if (!accumulator[branchId]) accumulator[branchId] = new Set<string>();
        accumulator[branchId].add(contactId);
      }
      Object.entries(accumulator).forEach(([branchId, contacts]) => {
        contactCounts[branchId] = contacts.size;
      });
    }
  }

  return { data: { branches, contactCounts }, error: null };
}

export async function insertClientBranch(params: {
  clientId: string;
  branchName?: string;
  position?: number | null;
  externalRef?: string | null;
  paymentTerms?: string | null;
  supabase?: SupabaseClient;
}): Promise<{ data: { id: string } | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const insert: any = {
    client_id: params.clientId,
    branch_name: params.branchName ?? 'New Branch',
    position: params.position ?? null,
  };
  if (params.externalRef !== undefined) {
    insert.external_ref = params.externalRef || null;
  }
  if (params.paymentTerms !== undefined) {
    insert.payment_terms = params.paymentTerms || null;
  }
  const { data, error } = await supabase.from('client_branches').insert(insert).select('id').single();
  if (error) return { data: null, error };
  return { data: data as any, error: null };
}

export type ClientBranchQuickCreateInput = {
  clientId: string;
  branchName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export async function createClientBranchQuick(
  input: ClientBranchQuickCreateInput,
  options?: ClientOption,
): Promise<{ data: ClientBranchDetail | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const branchName = input.branchName.trim();
  if (!input.clientId || !branchName) return { data: null, error: null };
  const payload = {
    client_id: input.clientId,
    branch_name: branchName,
    shipping_address_line1: input.addressLine1?.trim() || null,
    shipping_address_line2: input.addressLine2?.trim() || null,
    shipping_city: input.city?.trim() || null,
    shipping_state: input.state?.trim() || null,
    shipping_postal_code: input.postalCode?.trim() || null,
    shipping_country: input.country?.trim() || null,
    billing_address_line1: input.addressLine1?.trim() || null,
    billing_address_line2: input.addressLine2?.trim() || null,
    billing_city: input.city?.trim() || null,
    billing_state: input.state?.trim() || null,
    billing_postal_code: input.postalCode?.trim() || null,
    billing_country: input.country?.trim() || null,
  };
  const { data, error } = await supabase
    .from('client_branches')
    .insert(payload)
    .select(
      'id,client_id,branch_name,billing_address_line1,billing_address_line2,billing_city,billing_state,billing_postal_code,billing_country,shipping_address_line1,shipping_address_line2,shipping_city,shipping_state,shipping_postal_code,shipping_country,external_ref,set_discount_percent,payment_terms,position',
    )
    .maybeSingle();
  if (error || !data) return { data: null, error: error ?? null };
  const row = data as ClientBranchQuickRow;
  return {
    data: {
      id: String(row.id),
      client_id: String(row.client_id),
      branch_name: row.branch_name || 'Untitled Branch',
      billing_address_line1: row.billing_address_line1 ?? null,
      billing_address_line2: row.billing_address_line2 ?? null,
      billing_city: row.billing_city ?? null,
      billing_state: row.billing_state ?? null,
      billing_postal_code: row.billing_postal_code ?? null,
      billing_country: row.billing_country ?? null,
      shipping_address_line1: row.shipping_address_line1 ?? null,
      shipping_address_line2: row.shipping_address_line2 ?? null,
      shipping_city: row.shipping_city ?? null,
      shipping_state: row.shipping_state ?? null,
      shipping_postal_code: row.shipping_postal_code ?? null,
      shipping_country: row.shipping_country ?? null,
      external_ref: row.external_ref ?? null,
      set_discount_percent:
        row.set_discount_percent != null && !Number.isNaN(Number(row.set_discount_percent))
          ? Number(row.set_discount_percent)
          : null,
      payment_terms: row.payment_terms ?? null,
      position: row.position ?? null,
    },
    error: null,
  };
}

export async function updateClientBranch(params: {
  branchId: string;
  patch: Partial<ClientBranchDetail>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  if (!params.branchId) return null;
  const payload = Object.entries(params.patch || {}).reduce((acc, [key, value]) => {
    acc[key] = typeof value === 'string' && value.trim() === '' ? null : value;
    return acc;
  }, {} as Record<string, any>);
  const { error } = await supabase
    .from('client_branches')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', params.branchId);
  return error ?? null;
}

export async function archiveClientBranch(params: { branchId: string; supabase?: SupabaseClient }): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  if (!params.branchId) return null;
  const { error } = await supabase
    .from('client_branches')
    .update({ deleted_at: new Date().toISOString(), deleted_by: null })
    .eq('id', params.branchId);
  return error ?? null;
}

export const clientBranchesQueryKey = (clientId: string) => ['client-branches', clientId];

export const getClientBranchesQueryOptions = ({
  clientId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  clientId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: clientBranchesQueryKey(clientId),
  enabled: enabled && Boolean(clientId),
  queryFn: async () => {
    const { data, error } = await fetchClientBranchDetails(clientId, { supabase });
    if (error) throw error;
    return data;
  },
});

export async function syncContactBranchLinks(params: {
  contactId: string;
  currentBranchIds: string[];
  nextBranchIds: string[];
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const prevSet = new Set(params.currentBranchIds.map(String));
  const nextSet = new Set(params.nextBranchIds.map(String));
  const toRemove = Array.from(prevSet).filter(id => !nextSet.has(id));
  const toAdd = Array.from(nextSet).filter(id => !prevSet.has(id));
  if (toRemove.length) {
    const ids = toRemove.map(id => Number(id)).filter(num => Number.isFinite(num));
    if (ids.length) {
      const { error } = await supabase.from('contact_branch_links').delete().eq('contact_id', params.contactId).in('branch_id', ids);
      if (error) return error;
    }
  }
  if (toAdd.length) {
    const rows = toAdd
      .map(id => Number(id))
      .filter(num => Number.isFinite(num))
      .map(branchId => ({ contact_id: params.contactId, branch_id: branchId }));
    if (rows.length) {
      const { error } = await supabase.from('contact_branch_links').insert(rows);
      if (error) return error;
    }
  }
  return null;
}

type ClientContactInsert = {
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_type?: string | null;
  note?: string | null;
  position?: number | null;
  card_image_path?: string | null;
};

export async function insertClientContact(params: {
  clientId: string;
  values?: ClientContactInsert;
  supabase?: SupabaseClient;
}): Promise<{ data: { id: string } | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_contacts')
    .insert({ client_id: params.clientId, ...(params.values || {}) })
    .select('id')
    .single();
  return { data: data as any, error: error ?? null };
}

export async function updateClientContact(params: {
  contactId: string;
  patch: Partial<ClientContactInsert>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('client_contacts').update(params.patch).eq('id', params.contactId);
  return error ?? null;
}

export async function softDeleteClientContact(params: { contactId: string; supabase?: SupabaseClient }): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('soft_delete', { table_name: 'client_contacts', _id: params.contactId });
  return error ?? null;
}

type PhoneInput = { kind: 'cell' | 'office' | 'other'; number: string; is_primary?: boolean };
type EmailInput = { kind: 'work' | 'personal' | 'other'; email: string; is_primary?: boolean };

export async function replaceClientContactPhonesEmails(params: {
  contactId: string;
  phones: PhoneInput[];
  emails: EmailInput[];
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { contactId, phones, emails } = params;
  const { error: phoneDeleteError } = await supabase
    .from('contact_phones')
    .delete()
    .eq('entity', 'client')
    .eq('contact_id', contactId)
    .is('deleted_at', null);
  if (phoneDeleteError) return phoneDeleteError;
  const { error: emailDeleteError } = await supabase
    .from('contact_emails')
    .delete()
    .eq('entity', 'client')
    .eq('contact_id', contactId)
    .is('deleted_at', null);
  if (emailDeleteError) return emailDeleteError;

  if (phones.length) {
    let primaryIndex = phones.findIndex(p => p.is_primary);
    if (primaryIndex < 0) {
      primaryIndex = Math.max(0, phones.findIndex(p => p.kind === 'cell'));
      if (primaryIndex < 0) primaryIndex = 0;
    }
    const rows = phones
      .map((p, idx) => ({ entity: 'client', contact_id: contactId, kind: p.kind, number: String(p.number || '').trim(), is_primary: idx === primaryIndex }))
      .filter(r => r.number);
    if (rows.length) {
      const { error } = await supabase.from('contact_phones').insert(rows);
      if (error) return error;
    }
  }
  if (emails.length) {
    let primaryIndex = emails.findIndex(e => e.is_primary);
    if (primaryIndex < 0) primaryIndex = 0;
    const rows = emails
      .map((e, idx) => ({
        entity: 'client',
        contact_id: contactId,
        kind: e.kind,
        email: String(e.email || '').trim().toLowerCase(),
        is_primary: idx === primaryIndex,
      }))
      .filter(r => r.email);
    if (rows.length) {
      const { error } = await supabase.from('contact_emails').insert(rows);
      if (error) return error;
    }
  }
  return null;
}

export type ClientContactQuickCreateInput = {
  clientId: string;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  branchId?: string | null;
};

export async function createClientContactQuick(
  input: ClientContactQuickCreateInput,
  options?: ClientOption,
): Promise<{ data: ClientContactOption | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  if (!input.clientId) return { data: null, error: null };
  const { data, error } = await insertClientContact({
    clientId: input.clientId,
    values: {
      first_name: input.firstName?.trim() || null,
      last_name: input.lastName?.trim() || null,
      title: input.title?.trim() || null,
      email: input.email?.trim() || null,
      phone: input.phone?.trim() || null,
    },
    supabase,
  });
  if (error || !data?.id) return { data: null, error: error ?? null };

  const contactId = data.id;
  const phone = input.phone?.trim();
  const email = input.email?.trim();
  const replaceError = await replaceClientContactPhonesEmails({
    contactId,
    phones: phone ? [{ kind: 'office', number: phone, is_primary: true }] : [],
    emails: email ? [{ kind: 'work', email, is_primary: true }] : [],
    supabase,
  });
  if (replaceError) return { data: null, error: replaceError };

  if (input.branchId) {
    const linkError = await syncContactBranchLinks({
      contactId,
      currentBranchIds: [],
      nextBranchIds: [input.branchId],
      supabase,
    });
    if (linkError) return { data: null, error: linkError };
  }

  return {
    data: {
      id: contactId,
      firstName: input.firstName?.trim() || null,
      lastName: input.lastName?.trim() || null,
      title: input.title?.trim() || null,
      email: email || null,
      phone: phone || null,
      branchIds: input.branchId ? [input.branchId] : [],
    },
    error: null,
  };
}

export async function createContactCardSignedUrl(
  path: string,
  options?: ClientOption,
): Promise<{ url: string | null; error: StorageError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.storage.from('contact-cards').createSignedUrl(path, 60);
  return { url: data?.signedUrl ?? null, error: error ?? null };
}

export async function uploadContactCardImage(params: {
  clientId: string;
  contactId: string;
  file: File;
  supabase?: SupabaseClient;
}): Promise<{ path: string | null; error: StorageError | PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const ext = params.file.type && params.file.type.includes('png')
    ? 'png'
    : params.file.type && params.file.type.includes('webp')
      ? 'webp'
      : params.file.type && params.file.type.includes('heic')
        ? 'heic'
        : params.file.type && params.file.type.includes('heif')
          ? 'heif'
          : 'jpg';
  const path = `client/${params.clientId}/contacts/${params.contactId}.${ext}`;
  const contentType = params.file.type || 'image/jpeg';
  const { error: uploadError } = await supabase.storage.from('contact-cards').upload(path, params.file, {
    upsert: false,
    contentType,
  });
  if (uploadError) return { path: null, error: uploadError };
  const { error: updateError } = await supabase.from('client_contacts').update({ card_image_path: path }).eq('id', params.contactId);
  if (updateError) return { path: null, error: updateError };
  return { path, error: null };
}

export type ClientEventRow = {
  id: string;
  name: string | null;
  event_code: string;
  r2_contract_iid?: string | null;
  show_start_date: string | null;
  show_end_date?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  load_in_date?: string | null;
  setup_date?: string | null;
  rehearsal_date?: string | null;
  strike_date?: string | null;
  load_out_date?: string | null;
  status: string | null;
  client_id: string | null;
  client_business: string | null;
  subcontracted_for_is_active: boolean | null;
  subcontracted_for_client_id: string | null;
  subcontracted_for_business: string | null;
  site_location_id?: string | null;
  project_manager_profile_id?: string | null;
  sales_manager_id?: string | null;
  primary_contact_id?: string | null;
  contact?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  tax_subtotal?: number | null;
  customer_total_amount?: number | null;
  customer?: { business_name?: string | null } | null;
  project_manager?: { full_name?: string | null; first_name?: string | null; last_name?: string | null } | null;
  event_venues?: { venues?: { venue_name?: string | null } | null }[] | null;
};

export function clientEventCustomerName(event: ClientEventRow): string | null {
  return event.client_business?.trim()
    || event.customer?.business_name?.trim()
    || null;
}

export function clientEventContactName(event: ClientEventRow): string | null {
  const contact = event.contact;
  if (!contact) return null;
  return [contact.first_name, contact.last_name]
    .map((part) => part?.trim() || '')
    .filter(Boolean)
    .join(' ')
    || contact.email?.trim()
    || null;
}

export async function fetchClientEvents(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientEventRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const unique = new Map<string, ClientEventRow>();
  const pageSize = 500;
  let lastEventId: string | null = null;

  while (true) {
    let query = supabase
      .from('events')
      .select(
        'id,name,event_code,r2_contract_iid,show_start_date,show_end_date,event_start_date,event_end_date,load_in_date,setup_date,rehearsal_date,strike_date,load_out_date,status,client_id,client_business,subcontracted_for_is_active,subcontracted_for_client_id,subcontracted_for_business,site_location_id,project_manager_profile_id,sales_manager_id,primary_contact_id,tax_subtotal,project_manager:project_manager_profile_id(full_name,first_name,last_name),event_venues:event_venues_event_id_fkey(venues(venue_name))',
      )
      .is('deleted_at', null)
      .or(`client_id.eq.${clientId},and(subcontracted_for_is_active.is.true,subcontracted_for_client_id.eq.${clientId})`)
      .order('id', { ascending: true });
    if (lastEventId) query = query.gt('id', lastEventId);
    const { data, error } = await query.limit(pageSize);
    if (error) return { data: [], error };
    const page = (Array.isArray(data) ? data : []) as ClientEventRow[];
    for (const row of page) {
      if (!unique.has(row.id)) unique.set(row.id, row);
    }
    if (page.length < pageSize) break;
    const nextLastEventId = page[page.length - 1]?.id;
    if (!nextLastEventId || nextLastEventId === lastEventId) break;
    lastEventId = nextLastEventId;
  }

  const rows = Array.from(unique.values());
  const eventIds = rows.map((row) => row.id);
  if (eventIds.length === 0) return { data: [], error: null };

  const subtotalByEventId = new Map<string, unknown>();
  type ContactRow = NonNullable<ClientEventRow['contact']> & { event_id: string; position?: number | null };
  const contactsByEventId = new Map<string, ContactRow[]>();
  const hydrationBatchSize = 250;
  const batches: string[][] = [];
  for (let start = 0; start < eventIds.length; start += hydrationBatchSize) batches.push(eventIds.slice(start, start + hydrationBatchSize));
  try {
    await mapWithConcurrency(batches, 3, async batchEventIds => {
    const [totalsResult, contactsResult] = await Promise.all([
      supabase.rpc('get_event_totals_batch', { _event_ids: batchEventIds }),
      supabase
        .from('event_contacts')
        .select('id,event_id,first_name,last_name,email,position')
        .in('event_id', batchEventIds)
        .is('deleted_at', null)
        .order('position', { ascending: true }),
    ]);
    if (totalsResult.error) throw totalsResult.error;
    if (contactsResult.error) throw contactsResult.error;

    for (const total of Array.isArray(totalsResult.data) ? totalsResult.data : []) {
      if (typeof total?.event_id === 'string') {
        subtotalByEventId.set(total.event_id, total.amount_post_discount);
      }
    }
    for (const contact of (Array.isArray(contactsResult.data) ? contactsResult.data : []) as ContactRow[]) {
      const eventContacts = contactsByEventId.get(contact.event_id) ?? [];
      eventContacts.push(contact);
      contactsByEventId.set(contact.event_id, eventContacts);
    }
    });
  } catch (error) {
    return { data: [], error: error as PostgrestError };
  }

  return {
    data: rows.map((row) => {
      const eventContacts = contactsByEventId.get(row.id) ?? [];
      const contact = eventContacts.find((candidate) => candidate.id === row.primary_contact_id)
        ?? (eventContacts.length === 1 ? eventContacts[0] : null);
      return {
        ...row,
        contact,
        customer_total_amount: calculateEventCustomerTotalAmount(
          subtotalByEventId.get(row.id),
          row.tax_subtotal,
        ),
      };
    }),
    error: null,
  };
}

export type ClientImportPayload = {
  business_name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  tax_status?: string | null;
  notes?: string | null;
};

export async function insertClientFromImport(
  payload: ClientImportPayload,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('clients').insert({
    business_name: payload.business_name,
    address_line1: payload.address_line1 ?? null,
    address_line2: payload.address_line2 ?? null,
    city: payload.city ?? null,
    state: payload.state ?? null,
    postal_code: payload.postal_code ?? null,
    country: payload.country ?? null,
    tax_status: payload.tax_status ?? null,
    notes: payload.notes ?? null,
  });
  return error ?? null;
}

export async function fetchManagementCompanies(
  search?: string,
  options?: ClientOption,
): Promise<{ data: ClientSummary[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trimmed = (search || '').trim();
  let query = supabase
    .from('clients')
    .select('id,business_name,tax_status,city,state')
    .eq('is_management_co', true)
    .is('deleted_at', null);
  
  if (trimmed) {
    query = query.ilike('business_name', `%${trimmed}%`);
  }
  
  query = query.order('business_name', { ascending: true }).limit(100);
  
  const { data, error } = await query;
  return { data: Array.isArray(data) ? data.map(rowToClientSummary) : [], error: error ?? null };
}

export async function fetchVenueOwners(
  search?: string,
  options?: ClientOption,
): Promise<{ data: ClientSummary[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trimmed = (search || '').trim();
  let query = supabase
    .from('clients')
    .select('id,business_name,tax_status,city,state')
    .eq('is_venue_owner', true)
    .is('deleted_at', null);
  
  if (trimmed) {
    query = query.ilike('business_name', `%${trimmed}%`);
  }
  
  query = query.order('business_name', { ascending: true }).limit(100);
  
  const { data, error } = await query;
  return { data: Array.isArray(data) ? data.map(rowToClientSummary) : [], error: error ?? null };
}

export type ClientMarketingSegment = {
  segment_key: string;
  label: string;
  sort_order: number;
};

/**
 * Market segment vocabulary. Lives in a table rather than a hard-coded list so
 * it can change without a deploy; ordered by how often each segment is used so
 * the handful covering most of the book sit at the top of the picker.
 */
export async function fetchClientMarketingSegments(
  options?: ClientOption,
): Promise<{ data: ClientMarketingSegment[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_marketing_segments')
    .select('segment_key,label,sort_order')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  return { data: Array.isArray(data) ? (data as ClientMarketingSegment[]) : [], error: error ?? null };
}

export type MergeContact = {
  id: string;
  client_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
};

export type ContactResolution = {
  source_contact_id: string;
  target_contact_id: string;
  keep: 'source' | 'target';
};

export async function fetchContactsForMergePreview(
  sourceClientId: string,
  targetClientId: string,
  options?: ClientOption,
): Promise<{ source: MergeContact[]; target: MergeContact[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const cols = 'id,client_id,first_name,last_name,email,phone,title';

  const [srcRes, tgtRes] = await Promise.all([
    supabase
      .from('client_contacts')
      .select(cols)
      .eq('client_id', sourceClientId)
      .is('deleted_at', null)
      .order('email', { ascending: true, nullsFirst: false }),
    supabase
      .from('client_contacts')
      .select(cols)
      .eq('client_id', targetClientId)
      .is('deleted_at', null)
      .order('email', { ascending: true, nullsFirst: false }),
  ]);

  const firstError = srcRes.error ?? tgtRes.error ?? null;
  if (firstError) return { source: [], target: [], error: firstError };

  const mapRow = (r: any): MergeContact => ({
    id: r.id,
    client_id: r.client_id,
    first_name: r.first_name ?? null,
    last_name: r.last_name ?? null,
    email: r.email ?? null,
    phone: r.phone ?? null,
    title: r.title ?? null,
  });

  return {
    source: (srcRes.data ?? []).map(mapRow),
    target: (tgtRes.data ?? []).map(mapRow),
    error: null,
  };
}

export type ClientMergeImpact = { events: number; branches: number; iids: string[] };

/**
 * What a merge would move off one customer, for the merge dialog's pair card and review. It mirrors
 * merge_clients: events linked by client_id or bill_to_client_id (both indexed), active branches, and the R2
 * customer and suite IIDs on the row. Contacts come from fetchContactsForMergePreview.
 */
export async function fetchClientMergeImpact(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientMergeImpact | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const [events, branches, row] = await Promise.all([
    supabase.from('events').select('id', { count: 'exact', head: true })
      .or(`client_id.eq.${clientId},bill_to_client_id.eq.${clientId}`),
    supabase.from('client_branches').select('id', { count: 'exact', head: true })
      .eq('client_id', clientId).is('deleted_at', null),
    supabase.from('clients').select('r2_customer_iid,suite_iid').eq('id', clientId).maybeSingle(),
  ]);
  const error = events.error ?? branches.error ?? row.error ?? null;
  if (error) return { data: null, error };
  const ids = [...(row.data?.r2_customer_iid ?? []), ...(row.data?.suite_iid ?? [])].filter(Boolean) as string[];
  return { data: { events: events.count ?? 0, branches: branches.count ?? 0, iids: [...new Set(ids)] }, error: null };
}

export async function mergeClients(
  params: {
    sourceClientId: string;
    targetClientId: string;
    mergeAsBranch: boolean;
    masterFields?: Record<string, { value: any; source: 'source' | 'target' }>;
    contactResolutions?: ContactResolution[];
  },
  options?: ClientOption,
): Promise<{ data: any; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();

  // Get current user's profile ID
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();

  // Build master_fields jsonb
  let masterFieldsJsonb: any = {};
  if (params.masterFields) {
    for (const [key, { value, source }] of Object.entries(params.masterFields)) {
      masterFieldsJsonb[key] = value;
      masterFieldsJsonb[`${key}_source`] = source;
    }
  }

  const { data, error } = await supabase.rpc('merge_clients_with_event_lock_bypass', {
    p_source_client_id: params.sourceClientId,
    p_target_client_id: params.targetClientId,
    p_merge_as_branch: params.mergeAsBranch,
    p_master_fields: masterFieldsJsonb,
    p_requested_by: profileId || null,
    p_contact_resolutions: params.contactResolutions ?? [],
  });
  
  if (error) {
    return { data: null, error };
  }
  
  if (data && typeof data === 'object' && 'success' in data && !data.success) {
    return { 
      data: null, 
      error: { 
        message: data.error || 'Merge failed', 
        name: 'PostgrestError',
        code: 'MERGE_ERROR', 
        details: '', 
        hint: '' 
      } as PostgrestError 
    };
  }
  
  return { data: data || null, error: null };
}

export type ClientMergeHistoryRecord = {
  merge_id: string;
  source_client_id: string;
  target_client_id: string;
  merge_as_branch: boolean;
  branch_id: number | null;
  merged_by: string | null;
  merged_at: string;
  undone_at: string | null;
  undone_by: string | null;
  undo_note: string | null;
  contacts_moved: number;
  branches_moved: number;
  events_updated: number;
};

export async function fetchClientMergeHistory(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientMergeHistoryRecord[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_client_merge_history', {
    p_client_id: clientId,
  });
  
  if (error) {
    return { data: [], error };
  }
  
  return { data: (Array.isArray(data) ? data : []) as ClientMergeHistoryRecord[], error: null };
}

export async function undoClientMerge(
  mergeId: string,
  undoNote?: string | null,
  options?: ClientOption,
): Promise<{ data: any; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  
  // Get current user's profile ID
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const { data, error } = await supabase.rpc('undo_client_merge', {
    p_merge_id: mergeId,
    p_undone_by: profileId || null,
    p_undo_note: undoNote || null,
  });
  
  if (error) {
    return { data: null, error };
  }
  
  if (data && typeof data === 'object' && 'success' in data && !data.success) {
    return { 
      data: null, 
      error: { 
        message: data.error || 'Undo failed', 
        name: 'PostgrestError',
        code: 'UNDO_ERROR', 
        details: '', 
        hint: '' 
      } as PostgrestError 
    };
  }
  
  return { data: data || null, error: null };
}
